// Left panel — textarea, platform chips, analyse button, quick prediction
import {
    Box, Typography, TextField, Button, Divider,
    CircularProgress, LinearProgress, Paper, alpha, useTheme,
} from '@mui/material';
import {
    TextFields as TextFieldsIcon,
    Bolt as BoltIcon,
    CheckCircle as CheckCircleIcon,
    AutoAwesome as AutoAwesomeIcon,
    Link as LinkIcon,
    YouTube as YouTubeIcon,
    Instagram as InstagramIcon,
} from '@mui/icons-material';
import { ScoreRing } from './AnalyzePrimitives';
import { PLATFORMS, VIRALITY_COLORS } from './constants';

export default function InputPanel({
    text, onTextChange,
    selectedPlatform, onPlatformChange,
    onSubmit, loading, predLoading,
    prediction,
}) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const twitterLeft = text.length <= 280 ? 280 - text.length : null;
    const overTwitter = text.length > 280 ? text.length - 280 : null;
    const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;

    // URL detection
    const isUrl = /^https?:\/\//i.test(text.trim());
    const isYouTube = isUrl && /youtube\.com|youtu\.be/i.test(text);
    const isInstagram = isUrl && /instagram\.com/i.test(text);
    const urlIcon = isYouTube ? <YouTubeIcon sx={{ fontSize: 15, color: '#FF0000' }} />
        : isInstagram ? <InstagramIcon sx={{ fontSize: 15, color: '#E1306C' }} />
        : <LinkIcon sx={{ fontSize: 15, color: '#6366f1' }} />;
    const urlColor = isYouTube ? '#FF0000' : isInstagram ? '#E1306C' : '#6366f1';
    const urlLabel = isYouTube ? 'YouTube video detected'
        : isInstagram ? 'Instagram post detected'
        : 'URL detected';

    const CHAR_LIMITS = {
        Twitter: 280,
        Instagram: 2200,
        LinkedIn: 3000,
        Reddit: 40000,
        YouTube: 5000,
    };

    const scoreColor = prediction
        ? prediction.score >= 80 ? '#10b981' : prediction.score >= 60 ? '#f59e0b' : '#ef4444'
        : '#6366f1';

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                background: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.35)' : '0 2px 20px rgba(0,0,0,0.07)',
                display: 'flex',
                flexDirection: 'column',
                gap: 2.5,
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                    width: 40, height: 40, borderRadius: '10px', flexShrink: 0,
                    bgcolor: alpha('#6366f1', isDark ? 0.2 : 0.1),
                    color: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <TextFieldsIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                        Your Content
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Paste or type your social media post
                    </Typography>
                </Box>
            </Box>

            <Divider />

            {/* Textarea */}
            <TextField
                fullWidth multiline rows={10}
                placeholder="Paste your social media post here…"
                value={text}
                onChange={e => onTextChange(e.target.value)}
                variant="outlined"
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 2.5,
                        fontSize: '0.95rem',
                        lineHeight: 1.7,
                        background: isDark ? alpha('#fff', 0.03) : alpha('#6366f1', 0.02),
                        '& fieldset': { borderColor: theme.palette.divider },
                        '&:hover fieldset': { borderColor: alpha('#6366f1', 0.5) },
                        '&.Mui-focused fieldset': { borderColor: '#6366f1', borderWidth: 2 },
                    },
                }}
            />

            {/* Counter row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: -1.5 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    {text.length} chars · {wordCount} words
                </Typography>
                {twitterLeft !== null && (
                    <Typography variant="caption" sx={{ color: alpha('#1DA1F2', 0.85), fontWeight: 600 }}>
                        {twitterLeft} left for Twitter
                    </Typography>
                )}
                {overTwitter !== null && (
                    <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: 600 }}>
                        {overTwitter} over Twitter limit
                    </Typography>
                )}
            </Box>

            {/* URL detection banner */}
            {isUrl && (
                <Box sx={{
                    display: 'flex', alignItems: 'flex-start', gap: 1.25,
                    p: 1.5, borderRadius: '8px',
                    bgcolor: alpha(urlColor, isDark ? 0.1 : 0.06),
                    border: `1px solid ${alpha(urlColor, 0.25)}`,
                    borderLeft: `3px solid ${urlColor}`,
                }}>
                    {urlIcon}
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: urlColor, display: 'block', lineHeight: 1.3 }}>
                            {urlLabel} — we'll fetch the content
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                            Title, description and caption will be extracted and analysed automatically
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* Platform selection — small rectangular buttons */}
            <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.7, fontSize: '0.68rem', mb: 1, display: 'block' }}>
                    Target Platform
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {PLATFORMS.map(p => (
                        <Box
                            key={p.label}
                            onClick={() => onPlatformChange(prev => prev === p.label ? null : p.label)}
                            sx={{
                                px: 1.5, py: 0.5, borderRadius: '8px', cursor: 'pointer',
                                fontWeight: 600, fontSize: '0.78rem',
                                bgcolor: selectedPlatform === p.label ? p.color : p.bg,
                                color: selectedPlatform === p.label ? 'white' : p.color,
                                border: `1px solid ${alpha(p.color, 0.3)}`,
                                transition: 'all 0.18s',
                                userSelect: 'none',
                                '&:hover': { bgcolor: p.color, color: 'white', transform: 'translateY(-1px)' },
                            }}
                        >
                            {p.label}
                        </Box>
                    ))}
                </Box>

                {/* Char limit info row — shown when a platform is selected */}
                {selectedPlatform && CHAR_LIMITS[selectedPlatform] != null && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
                            {selectedPlatform} limit:
                        </Typography>
                        <Typography variant="caption" sx={{
                            fontWeight: 700, fontSize: '0.72rem',
                            color: text.length > CHAR_LIMITS[selectedPlatform] ? '#ef4444' : 'text.secondary',
                        }}>
                            {text.length} / {CHAR_LIMITS[selectedPlatform].toLocaleString()} chars
                        </Typography>
                        {text.length > CHAR_LIMITS[selectedPlatform] && (
                            <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: 600, fontSize: '0.72rem' }}>
                                ({text.length - CHAR_LIMITS[selectedPlatform]} over limit)
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>

            {/* Submit button */}
            <Button
                fullWidth variant="contained" size="large"
                onClick={onSubmit}
                disabled={loading || predLoading || !text.trim()}
                startIcon={(loading || predLoading) ? <CircularProgress size={18} color="inherit" /> : <BoltIcon />}
                sx={{
                    borderRadius: '10px', py: 1.7,
                    fontWeight: 800, fontSize: '1rem', letterSpacing: 0.4,
                    background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                    boxShadow: '0 6px 20px rgba(99,102,241,0.4)',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #4338ca, #6d28d9)',
                        boxShadow: '0 8px 28px rgba(99,102,241,0.5)',
                        transform: 'translateY(-2px)',
                    },
                    '&:disabled': { background: alpha('#6366f1', 0.35), color: 'rgba(255,255,255,0.6)', boxShadow: 'none' },
                    transition: 'all 0.2s ease',
                }}
            >
                {(loading || predLoading) ? 'Analysing…' : isUrl ? 'Fetch & Analyse' : 'Analyse Content'}
            </Button>

            {/* ── Quick Prediction (inline) ──────────────────────────────────────── */}
            {(prediction || predLoading) && (
                <Box sx={{
                    borderRadius: '10px', overflow: 'hidden',
                    border: `1px solid ${alpha('#6366f1', isDark ? 0.25 : 0.15)}`,
                }}>
                    {/* Header bar */}
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        px: 2, py: 1,
                        background: alpha('#6366f1', isDark ? 0.15 : 0.07),
                        borderBottom: `1px solid ${alpha('#6366f1', isDark ? 0.2 : 0.12)}`,
                    }}>
                        <AutoAwesomeIcon sx={{ fontSize: 14, color: '#6366f1' }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#6366f1', fontSize: '0.72rem', letterSpacing: 0.5 }}>
                            QUICK PREDICTION
                        </Typography>
                    </Box>

                    <Box sx={{ p: 2 }}>
                        {predLoading ? (
                            <Box>
                                <LinearProgress sx={{ borderRadius: '4px', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg,#6366f1,#8b5cf6)' } }} />
                                <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block', textAlign: 'center' }}>
                                    Computing…
                                </Typography>
                            </Box>
                        ) : prediction && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <ScoreRing value={prediction.score} max={100} color={scoreColor} size={72} label="Score" />
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.25 }}>
                                        <Box sx={{
                                            px: 1.25, py: 0.4, borderRadius: '6px',
                                            bgcolor: VIRALITY_COLORS[prediction.virality]?.bg,
                                            border: `1px solid ${alpha(VIRALITY_COLORS[prediction.virality]?.text || '#6366f1', 0.3)}`,
                                        }}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', color: VIRALITY_COLORS[prediction.virality]?.text }}>
                                                {prediction.virality} Virality
                                            </Typography>
                                        </Box>
                                        <Box sx={{
                                            px: 1.25, py: 0.4, borderRadius: '6px',
                                            background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                                        }}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', color: 'white' }}>
                                                Best: {prediction.best}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                                        {prediction.suggestions.slice(0, 2).map((s, i) => (
                                            <Box key={i} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                                                <CheckCircleIcon sx={{ color: '#6366f1', fontSize: 12, mt: 0.35, flexShrink: 0 }} />
                                                <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5, fontSize: '0.72rem' }}>
                                                    {s}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            )}
        </Paper>
    );
}
